import { useQueries } from "@tanstack/react-query";
import {
  getOrdersToProcessCount,
  getOrdersToPickCount,
  getIssueOrdersCount,
  getClickAndCollectCount,
  getLastOrderTimeStamp,
} from "../../services";
import { globalTheme } from "../../styles";

export const useOrdersCount = () => {
  /**
   * Use useQueries hook to make parallel independant api calls
   * Ref: https://tanstack.com/query/latest/docs/framework/react/guides/parallel-queries
   * Ref: https://stackoverflow.com/questions/66427968/how-to-handle-multiple-queries-with-react-query
   * */
  const [ordersToPickCountQuery, orderToProcessCountQuery, issueOrdersCountQuery, clickAndCollectCountquery, lastTimeStampQuery] =
    useQueries({
      queries: [
        {
          queryKey: ["ordersToPickCount"],
          queryFn: async () => await getOrdersToPickCount(),
          enabled: true,
          refetchOnWindowFocus: false,
          refetchOnMount: false,
          retry: true,
          refetchInterval: 10000, // Refetch this data every 30 secs
        },
        {
          queryKey: ["ordersToProcessCount"],
          queryFn: async () => await getOrdersToProcessCount(),
          enabled: true,
          refetchOnWindowFocus: false,
          refetchOnMount: false,
          retry: false,
          refetchInterval: 10000, // Refetch this data every 30 secs
        },
        {
          queryKey: ["issueOrdersCount"],
          queryFn: async () => await getIssueOrdersCount(),
          enabled: true,
          refetchOnWindowFocus: false,
          refetchOnMount: false,
          retry: false,
          refetchInterval: 10000, // Refetch this data every 30 secs
        },
        {
          queryKey: ["clickAndCollectCountquery"],
          queryFn: async () => await getClickAndCollectCount(),
          enabled: true,
          refetchOnWindowFocus: false,
          refetchOnMount: false,
          retry: false,
          refetchInterval: 10000, // Refetch this data every 30 secs
        },
        {
          queryKey: ["lastTimeStampQuery"],
          queryFn: async () => await getLastOrderTimeStamp(),
          enabled: true,
          refetchOnWindowFocus: false,
          refetchOnMount: false,
          retry: false,
          refetchInterval: 10000, // Refetch this data every 30 secs
        },
      ],
    });

  // TODO: handle error states of orders count
  const {
    isLoading: isLoadingOrdersToPickCount,
    isFetching: isFetchingOrdersToPickCount,
    isError: isErrorOrdersToPickCount,
    data: ordersToPickCount,
    error: ordersToPickCountError,
    refetch: refetchOrdersToPickCount,
  } = ordersToPickCountQuery;

  const {
    isLoading: isLoadingOrdersToProcessCount,
    isFetching: isFetchingOrdersToProcessCount,
    isError: isErrorOrdersToProcessCount,
    data: ordersToProcessCount,
    error: ordersToProcessCountError,
    refetch: refetchOrdersToProcessCount,
  } = orderToProcessCountQuery;

  const {
    isLoading: isLoadingIssueOrdersCount,
    isFetching: isFetchingIssueOrdersCount,
    isError: isErrorIssueOrdersCount,
    data: issueOrdersCount,
    error: issueOrdersCountError,
    refetch: refetchIssueOrdersCount,
  } = issueOrdersCountQuery;

  const {
    isLoading: isLoadingclickandCollectOrder,
    isFetching: isFetchingclickandCollectOrder,
    isError: isErrorclickandCollectOrder,
    data: clickandCollectOrdercount,
    error: clickandCollectOrderError,
    refetch: refetchclickandCollectOrder,
  } = clickAndCollectCountquery;

  const {
    isLoading: isLoadinglastOrderTimeStampOrder,
    isFetching: isFetchinglastOrderTimeStampOrder,
    isError: isErrorlastOrderTimeStampOrder,
    data: lastOrderTimeStampOrdercount,
    error: lastOrderTimeStampOrderError,
    refetch: refetchlastOrderTimeStampOrder,
  } = lastTimeStampQuery;

  const orderStatData = [
    {
      title: "Orders to pick",
      count: isFetchingOrdersToPickCount ? undefined : ordersToPickCount,
      colorCode: globalTheme.activeMenu,
      bgColor: null,
      borderColor: `1px solid ${globalTheme.border}`,
      isError: isErrorOrdersToPickCount,
      loading: isLoadingOrdersToPickCount,
      refetchCardStats: refetchOrdersToPickCount,
      error: ordersToPickCountError,
    },
    {
      title: "Click & Collect Orders",
      count: isFetchingclickandCollectOrder ? undefined : clickandCollectOrdercount,
      colorCode: globalTheme.activeMenu,
      bgColor: null,
      borderColor: `1px solid ${globalTheme.border}`,
      isError: isErrorOrdersToPickCount,
      loading: isLoadingOrdersToPickCount,
      refetchCardStats: refetchOrdersToPickCount,
      error: ordersToPickCountError,
    },
    {
      title: "Orders to process",
      count: isFetchingOrdersToProcessCount ? undefined : ordersToProcessCount,
      colorCode: globalTheme.orange,
      bgColor: null,
      borderColor: `1px solid ${globalTheme.border}`,
      isError: isErrorOrdersToProcessCount,
      loading: isLoadingOrdersToProcessCount,
      refetchCardStats: refetchOrdersToProcessCount,
      error: ordersToProcessCountError,
    },
    {
      title: "Orders in issue",
      count: isFetchingIssueOrdersCount ? undefined : issueOrdersCount,
      colorCode: globalTheme.red,
      bgColor: null,
      borderColor: `1px solid ${globalTheme.border}`,
      isError: isErrorIssueOrdersCount,
      loading: isLoadingIssueOrdersCount,
      refetchCardStats: refetchIssueOrdersCount,
      error: issueOrdersCountError,
    },
  ];

  const lastOrderTimeStamp = [
    {
      title: "Last Sync Timestamp ",
      count: isFetchinglastOrderTimeStampOrder ? undefined : lastOrderTimeStampOrdercount,
      colorCode: globalTheme.red,
      bgColor: null,
      borderColor: `1px solid ${globalTheme.border}`,
      isError: isErrorlastOrderTimeStampOrder,
      loading: isLoadinglastOrderTimeStampOrder,
      refetchCardStats: refetchlastOrderTimeStampOrder,
      error: lastOrderTimeStampOrderError,
    },
  ];
  return { lastOrderTimeStamp, orderStatData };
};
